import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity,
    ArrowUpRight,
    BarChart3,
    CalendarClock,
    ClipboardList,
    LayoutDashboard,
    Package,
    Receipt,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PhaseBadge } from "@/components/PhaseBadge";
import { DviUpsellTile } from "@/components/dashboard/DviUpsellTile";
import { ServiceRemindersTile } from "@/components/dashboard/ServiceRemindersTile";
import { AnalyticsTab } from "@/components/dashboard/AnalyticsTab";
import { useAuth } from "@/contexts/AuthContext";
import { useT } from "@/lib/i18n";
import { useFormatters } from "@/lib/format";
import { AppointmentAPI, JobCardAPI, StockAPI, InvoiceAPI } from "@/lib/api";

export default function DashboardPage() {
    const { user, tenant, branches, currentBranchId, permissions } = useAuth();
    const { formatDateTime } = useFormatters();
    const { t } = useT();
    const currentBranch = branches.find((b) => b.id === currentBranchId);
    const canJobs = permissions.includes("jobcard:read");
    const canAppts = permissions.includes("appointment:read");
    const canStock = permissions.includes("part:read") || permissions.includes("inventory:adjust");
    const canInvoices = permissions.includes("invoice:read");

    const [jobCounts, setJobCounts] = useState(null);
    const [upcomingAppts, setUpcomingAppts] = useState([]);
    const [lowStock, setLowStock] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const calls = [];
        if (canJobs) calls.push(JobCardAPI.counts().then(setJobCounts).catch(() => setJobCounts({ booked: 0, in_progress: 0, total: 0 })));
        if (canAppts) {
            const now = new Date();
            const start = new Date(now); start.setHours(0, 0, 0, 0);
            const end = new Date(now); end.setDate(end.getDate() + 7); end.setHours(23, 59, 59, 999);
            calls.push(
                AppointmentAPI.list({ start_from: start.toISOString(), start_to: end.toISOString(), page_size: 20, status: ["scheduled", "confirmed", "checked_in"] })
                    .then((r) => setUpcomingAppts(r.items || []))
                    .catch(() => setUpcomingAppts([]))
            );
        }
        if (canStock) {
            calls.push(
                StockAPI.lowStockAlerts({ branch_id: currentBranchId })
                    .then((r) => setLowStock({
                        total: (r.items || []).length,
                        critical: r.critical || 0,
                        low: r.low || 0,
                        items: (r.items || []).slice(0, 6),
                    }))
                    .catch(() => setLowStock({ total: 0, critical: 0, low: 0, items: [] }))
            );
        }
        if (canInvoices) {
            calls.push(
                InvoiceAPI.summary({ branch_id: currentBranchId })
                    .then(setSummary)
                    .catch(() => setSummary({
                        revenue_today_minor: 0, revenue_week_minor: 0, revenue_month_minor: 0,
                        outstanding_receivables_minor: 0, estimates_awaiting_count: 0,
                    }))
            );
        }
        Promise.allSettled(calls).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentBranchId]);

    const activeJobs = jobCounts ? (jobCounts.booked || 0) + (jobCounts.in_progress || 0) + (jobCounts.awaiting_approval || 0) + (jobCounts.awaiting_parts || 0) + (jobCounts.qc || 0) + (jobCounts.ready || 0) : null;

    const STAT_CARDS = [
        {
            key: "todays_jobs", icon: ClipboardList, phase: null, label: t("dash.todays_jobs"),
            note: "Open job cards across the workshop",
            value: canJobs ? (activeJobs ?? "—") : "—",
            to: canJobs ? "/jobs" : null,
        },
        {
            key: "appointments", icon: CalendarClock, phase: null, label: t("dash.appointments"),
            note: "Bookings next 7 days",
            value: canAppts ? (upcomingAppts?.length ?? "—") : "—",
            to: canAppts ? "/schedule" : null,
        },
        {
            key: "low_stock", icon: Package, phase: null, label: t("dash.low_stock"),
            note: lowStock
                ? `${lowStock.critical} critical · ${lowStock.low} low`
                : "Parts below reorder level",
            value: canStock ? (lowStock?.total ?? "—") : "—",
            to: canStock ? "/inventory?tab=alerts" : null,
        },
        {
            key: "revenue", icon: Receipt, phase: null, label: t("dash.revenue"),
            note: summary
                ? `Today ₹${Math.round((summary.revenue_today_minor || 0) / 100).toLocaleString("en-IN")} · Week ₹${Math.round((summary.revenue_week_minor || 0) / 100).toLocaleString("en-IN")}`
                : "Revenue this month",
            value: canInvoices ? (summary ? `₹${Math.round((summary.revenue_month_minor || 0) / 100).toLocaleString("en-IN")}` : "—") : "—",
            to: canInvoices ? "/invoices?status=paid" : null,
            testid_value: "dashboard-revenue-today",
        },
    ];

    const EXTRA_CARDS = [
        {
            key: "outstanding", icon: ArrowUpRight, label: "Outstanding receivables",
            note: "Issued / Partially paid / Overdue",
            value: canInvoices && summary ? `₹${Math.round((summary.outstanding_receivables_minor || 0) / 100).toLocaleString("en-IN")}` : "—",
            to: canInvoices ? "/invoices?status=issued" : null,
            testid: "dashboard-outstanding-receivables",
        },
        {
            key: "awaiting", icon: ClipboardList, label: "Estimates awaiting approval",
            note: "Sent or partially approved",
            value: canInvoices && summary ? String(summary.estimates_awaiting_count || 0) : "—",
            to: canInvoices ? "/estimates?status=sent_for_approval" : null,
            testid: "dashboard-estimates-awaiting",
        },
    ];

    return (
        <div className="animate-fade-in space-y-8" data-testid="dashboard">
            <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                        {tenant?.name} · {currentBranch?.code}
                    </div>
                    <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                        {t("dash.welcome")}, {user?.name?.split(" ")[0]}
                    </h1>
                    <p className="mt-1 max-w-2xl text-sm text-zinc-500">
                        Your workshop at a glance — jobs, appointments, inventory and billing in one place.
                    </p>
                </div>
                <span className="inline-flex items-center gap-2 rounded border border-brand-redSoft bg-brand-redSoft px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-brand-redDeep dark:border-brand-red/40 dark:bg-brand-red/10 dark:text-brand-red">
                    <Activity className="h-3 w-3" />
                    {user?.role}
                </span>
            </header>

            <Tabs defaultValue="overview">
                <TabsList data-testid="dashboard-tabs">
                    <TabsTrigger value="overview" data-testid="dashboard-tab-overview"><LayoutDashboard className="mr-1.5 h-3.5 w-3.5" />Overview</TabsTrigger>
                    <TabsTrigger value="analytics" data-testid="dashboard-tab-analytics"><BarChart3 className="mr-1.5 h-3.5 w-3.5" />Analytics</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-6 space-y-8">
            <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <DviUpsellTile className="bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-900/20 dark:to-zinc-900" />
                <ServiceRemindersTile />
            </section>

            <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {STAT_CARDS.map((c) => {
                    const isLink = !!c.to;
                    const Wrapper = isLink ? Link : "div";
                    const wrapperProps = isLink ? { to: c.to } : {};
                    return (
                        <Wrapper key={c.key} {...wrapperProps} className={isLink ? "group" : ""}>
                            <Card
                                data-testid={`stat-${c.key}`}
                                className={`flex h-full flex-col gap-3 p-5 ${isLink ? "transition-shadow group-hover:shadow-md group-hover:border-brand-red/50" : "dashed-card"}`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="grid h-9 w-9 place-items-center rounded border border-zinc-200 bg-white text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                                        <c.icon className="h-4 w-4" />
                                    </span>
                                    {c.phase != null
                                        ? <PhaseBadge phase={c.phase} label={`${t("dash.placeholder")} ${c.phase}`} />
                                        : isLink && <ArrowUpRight className="h-3.5 w-3.5 text-zinc-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-brand-red" />}
                                </div>
                                <div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                                        {c.label}
                                    </div>
                                    <div className={`mt-1 font-display text-4xl font-bold tabular-nums ${c.phase != null ? "text-zinc-300 dark:text-zinc-700" : "text-zinc-900 dark:text-zinc-50"}`}
                                          data-testid={c.testid_value || undefined}>
                                        {loading && c.phase == null ? "…" : c.value}
                                    </div>
                                    <div className="mt-2 text-xs text-zinc-500">{c.note}</div>
                                </div>
                            </Card>
                        </Wrapper>
                    );
                })}
            </section>

            {canInvoices && (
                <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {EXTRA_CARDS.map((c) => {
                        const isLink = !!c.to;
                        const Wrapper = isLink ? Link : "div";
                        const wrapperProps = isLink ? { to: c.to } : {};
                        return (
                            <Wrapper key={c.key} {...wrapperProps} className={isLink ? "group" : ""}>
                                <Card data-testid={c.testid}
                                       className={`flex h-full flex-col gap-3 p-5 ${isLink ? "transition-shadow group-hover:shadow-md group-hover:border-brand-red/50" : ""}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="grid h-9 w-9 place-items-center rounded border border-zinc-200 bg-white text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200">
                                            <c.icon className="h-4 w-4" />
                                        </span>
                                        {isLink && <ArrowUpRight className="h-3.5 w-3.5 text-zinc-400 transition-transform group-hover:text-brand-red" />}
                                    </div>
                                    <div>
                                        <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{c.label}</div>
                                        <div className="mt-1 font-display text-3xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">
                                            {loading ? "…" : c.value}
                                        </div>
                                        <div className="mt-2 text-xs text-zinc-500">{c.note}</div>
                                    </div>
                                </Card>
                            </Wrapper>
                        );
                    })}
                </section>
            )}

            <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="p-5 lg:col-span-2" data-testid="upcoming-appts-card">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-lg font-bold tracking-tight">Upcoming appointments</h2>
                        <Link to="/schedule" className="font-mono text-[10px] uppercase tracking-wider text-brand-redDeep hover:underline dark:text-brand-red">
                            Open schedule →
                        </Link>
                    </div>
                    {canAppts && upcomingAppts.length === 0 ? (
                        <p className="mt-6 text-sm text-zinc-500">No bookings in the next 7 days. <Link to="/appointments/new" className="text-brand-redDeep hover:underline">Book one →</Link></p>
                    ) : (
                        <ul className="mt-4 divide-y divide-zinc-100 dark:divide-zinc-800">
                            {upcomingAppts.slice(0, 6).map((a) => (
                                <li key={a.id}>
                                    <Link to={`/appointments/${a.id}`} className="flex items-center justify-between py-2.5 text-sm hover:bg-zinc-50 dark:hover:bg-zinc-900/40">
                                        <div>
                                            <div className="font-medium">{a.customer?.display_name || "Walk-in"}</div>
                                            <div className="font-mono text-[11px] text-zinc-500">{a.vehicle?.registration_number || "—"} · {formatDateTime(a.scheduled_start, { options: { dateStyle: "medium", timeStyle: "short", hour12: undefined } })}</div>
                                        </div>
                                        <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{a.status}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </section>
                </TabsContent>

                <TabsContent value="analytics" className="mt-6">
                    <AnalyticsTab />
                </TabsContent>
            </Tabs>
        </div>
    );
}
